import { array, number, option, ord } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { createSelector } from 'reselect';
import { createReduceAndSumAmounts, multiplyAmount } from '@mews-distributor/common';
import { getStringProp, values } from '@mews/fp';
import { parentSelector } from '../../selectors/parentSelector';
import { ApplicationState } from '../../types';
import { getCurrencyCode } from '../app/selectors';
import { getPromotedServiceCategory } from '../promotedServiceCategories';
import { getSelectedPromotedServices } from '../resourceOrder/selectors';
import { PromotedService } from './types';

const byOrdering = pipe(
    number.Ord,
    ord.contramap((promotion: PromotedService) => promotion.ordering)
);

const getPromotedServices = createSelector(parentSelector, (state) => state.data.promotedServices);

export const getPromotedServicesData = createSelector(getPromotedServices, (promotedServices) => promotedServices.data);
const getPromotedServicesMeta = createSelector(getPromotedServices, (promotedServices) => promotedServices.meta);

export const hasPromotedServicesFailed = flow(getPromotedServicesMeta, ({ hasFailed }) => hasFailed);
export const arePromotedServicesFetching = flow(getPromotedServicesMeta, ({ isFetching }) => isFetching);

export const getPromotedServiceIds = createSelector(getPromotedServicesData, (promotedServices) =>
    pipe(
        promotedServices,
        values,
        array.sortBy([byOrdering]),
        array.map(({ promotedServiceAssignmentId }) => promotedServiceAssignmentId)
    )
);

export const getPromotedService = createSelector(
    getPromotedServicesData,
    getStringProp('promotedServiceId'),
    (promotedServices, promotedServiceId): PromotedService | undefined => promotedServices[promotedServiceId]
);

const getPromotedServiceCategoryData = (
    state: ApplicationState,
    { promotedServiceId }: { promotedServiceId: string }
) => {
    const promotedService = getPromotedService(state, { promotedServiceId });
    if (promotedService) {
        return getPromotedServiceCategory(state, { categoryId: promotedService.promotedResourceCategoryId });
    }
};

export const getPromotedServiceName = createSelector(getPromotedServiceCategoryData, (category) => category?.name);
export const getPromotedServiceDescription = createSelector(
    getPromotedServiceCategoryData,
    (category) => category?.description
);

export const getPromotedServiceAvailability = flow(
    getPromotedService,
    (promotedService) => promotedService?.availableCount ?? 0
);

export const hasPromotedServices = flow(getPromotedServiceIds, array.isNonEmpty);

export const doesAnyPromotedServiceHaveImage = createSelector(
    getPromotedServicesData,
    flow(
        values,
        array.some((promotedService) => promotedService.imageId !== null)
    )
);

export const getSelectedPromotedService = createSelector(
    getStringProp('promotedServiceId'),
    getSelectedPromotedServices,
    (selectedId, selectedPromotedServices) => selectedPromotedServices.find(({ id }) => id === selectedId)
);

export const isPromotedServiceSelected = createSelector(
    getSelectedPromotedService,
    (selectedPromotedService) => selectedPromotedService != null
);

export const getPromotedServicePricing = createSelector(
    getSelectedPromotedServices,
    getPromotedServicesData,
    getCurrencyCode,
    (selectedPromotedServices, promotedServices, currencyCode) =>
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        currencyCode
            ? pipe(
                  selectedPromotedServices,
                  array.filter((service) => promotedServices[service.id] != null),
                  array.map((service) => {
                      const totalAmount = promotedServices[service.id].totalAmount?.[currencyCode];

                      return option.fromNullable(
                          totalAmount != null ? multiplyAmount(totalAmount, service.count) : null
                      );
                  }),
                  array.compact,
                  createReduceAndSumAmounts(currencyCode)
              )
            : undefined
);
